import React, { useState, useEffect } from "react";
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiCopy, FiChevronDown, FiChevronUp } from "react-icons/fi";
import api from "../../api";
import "./EmailTemplates.css";

const EmailTemplates = ({ showToast }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showVariablesHelp, setShowVariablesHelp] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    body: "",
    is_shared: false
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get("/email-templates");
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      showToast?.("Failed to load email templates", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingTemplate(null);
    setFormData({
      name: "",
      subject: "",
      body: "",
      is_shared: false
    });
  };

  const handleEdit = (template) => {
    setEditingTemplate(template.id);
    setIsCreating(false);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      is_shared: template.is_shared
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingTemplate(null);
    setFormData({
      name: "",
      subject: "",
      body: "",
      is_shared: false
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      showToast?.("Please fill in all required fields", "error");
      return;
    }

    try {
      if (isCreating) {
        await api.post("/email-templates", formData);
        showToast?.("Template created successfully", "success");
      } else if (editingTemplate) {
        await api.put(`/email-templates/${editingTemplate}`, formData);
        showToast?.("Template updated successfully", "success");
      }
      
      fetchTemplates();
      handleCancel();
    } catch (error) {
      console.error("Error saving template:", error);
      showToast?.("Failed to save template", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      await api.delete(`/email-templates/${id}`);
      showToast?.("Template deleted successfully", "success");
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      showToast?.("Failed to delete template", "error");
    }
  };

  const handleDuplicate = (template) => {
    setIsCreating(true);
    setEditingTemplate(null);
    setFormData({
      name: `${template.name} (Copy)`,
      subject: template.subject,
      body: template.body,
      is_shared: false
    });
  };

  if (loading) {
    return (
      <div className="email-templates-container">
        <div className="loading-message">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="email-templates-container">
      <div className="email-templates-header">
        <h1>Email Templates</h1>
        <button className="create-template-btn" onClick={handleCreate}>
          <FiPlus /> New Template
        </button>
      </div>

      {(isCreating || editingTemplate) && (
        <div className="template-form-card">
          <div className="template-form-header">
            <h2>{isCreating ? "Create New Template" : "Edit Template"}</h2>
            <button className="icon-btn" onClick={handleCancel} title="Cancel">
              <FiX size={20} />
            </button>
          </div>
          
          <div className="template-form">
            <div className="form-group">
              <label>Template Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Follow-up Template"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Subject *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Email subject line"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Body *</label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Email body content..."
                className="form-textarea"
                rows={12}
              />
              <div className="variables-help-section">
                <button 
                  type="button"
                  className="variables-toggle"
                  onClick={() => setShowVariablesHelp(!showVariablesHelp)}
                >
                  {showVariablesHelp ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                  Available Variables
                </button>
                
                {showVariablesHelp && (
                  <div className="variables-list">
                    <div className="variables-category">
                      <strong>Contact Variables:</strong>
                      <ul>
                        <li><code>{"{{name}}"}</code> - Full name of the contact</li>
                        <li><code>{"{{firstName}}"}</code> - First name only</li>
                        <li><code>{"{{lastName}}"}</code> - Last name only</li>
                        <li><code>{"{{email}}"}</code> - Contact's email address</li>
                        <li><code>{"{{company}}"}</code> - Contact's company name</li>
                        <li><code>{"{{phone}}"}</code> - Contact's phone number</li>
                        <li><code>{"{{status}}"}</code> - Contact's status</li>
                      </ul>
                    </div>
                    <div className="variables-category">
                      <strong>Your Information:</strong>
                      <ul>
                        <li><code>{"{{myName}}"}</code> - Your name</li>
                        <li><code>{"{{myEmail}}"}</code> - Your email address</li>
                        <li><code>{"{{myCompany}}"}</code> - Your company name</li>
                      </ul>
                    </div>
                    <div className="variables-category">
                      <strong>Email Context:</strong>
                      <ul>
                        <li><code>{"{{subject}}"}</code> - Original email subject (when replying)</li>
                      </ul>
                    </div>
                    <p className="variables-note">
                      <em>Note: Variables are case-insensitive. If a variable has no value, it will be replaced with an empty string.</em>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_shared}
                  onChange={(e) => setFormData({ ...formData, is_shared: e.target.checked })}
                />
                <span>Share with team (visible to all users)</span>
              </label>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSave}>
                <FiSave /> Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="templates-grid">
        {templates.length === 0 ? (
          <div className="empty-state">
            <p>No templates yet. Create your first email template to get started!</p>
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="template-card">
              <div className="template-card-header">
                <h3>{template.name}</h3>
                {template.is_shared && (
                  <span className="shared-badge">Shared</span>
                )}
              </div>
              
              <div className="template-card-content">
                <div className="template-field">
                  <strong>Subject:</strong>
                  <p>{template.subject}</p>
                </div>
                <div className="template-field">
                  <strong>Body:</strong>
                  <p className="template-body-preview">{template.body}</p>
                </div>
              </div>

              <div className="template-card-actions">
                <button
                  className="icon-btn"
                  onClick={() => handleDuplicate(template)}
                  title="Duplicate"
                >
                  <FiCopy />
                </button>
                <button
                  className="icon-btn"
                  onClick={() => handleEdit(template)}
                  title="Edit"
                >
                  <FiEdit2 />
                </button>
                <button
                  className="icon-btn danger"
                  onClick={() => handleDelete(template.id)}
                  title="Delete"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EmailTemplates;

